import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { ITenant } from '@/types/Tenant'

/*
 * THE TENANT BEING WORKED IN — "Prostor stranke".
 *
 * Every tenant page lives under `/t/:tid/…`, so the ADDRESS is what names the tenant; this store
 * only holds the Tenant object for it (its name for the chrome, its brand for every canvas) and
 * the list the selector offers. A tenant user's list is their own active tenants (GET /tenants —
 * one or more, one per membership); a provider admin's is every tenant. The one picked last is
 * remembered per browser under `tenant`, so the console opens where they left — for a tenant
 * user only while it is still one of theirs (the browser may be shared, a membership removed).
 */
const STORAGE_KEY = 'tenant'

const readStored = (): string => {
    try {
        return localStorage.getItem(STORAGE_KEY) ?? ''
    } catch {
        return ''
    }
}

export const useTenantStore = defineStore('tenant', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const tenant         = ref<ITenant | null>(null)
    const tenants        = ref<ITenant[]>([])
    const tenantsLoaded  = ref(false)
    const loadFailed     = ref(false)
    const storedTid      = ref(readStored())

    /** The tenant id of the current tenant, '' while none is loaded. */
    const tid = computed((): string => tenant.value?.id ?? '')

    /** The remembered tenant when this person may still open it, else ''. */
    const rememberedTid = computed((): string => {
        const stored = storedTid.value
        if (!stored) return ''

        return appStore.isProviderAdmin() || appStore.membershipIn(stored) ? stored : ''
    })

    /*
     * Where a menu link to a tenant page points when the current page is not one (Stranke,
     * Nastavitve): the tenant open last, else the remembered one, else a tenant user's first.
     */
    const contextTid = computed((): string => tid.value || rememberedTid.value || appStore.firstTenantId())

    const remember = (id: string) => {
        storedTid.value = id

        try {
            localStorage.setItem(STORAGE_KEY, id)
        } catch {
            // Private mode: the choice lasts for this tab only.
        }
    }

    let pendingList: Promise<void> | null = null

    /** GET /tenants — sorted by name by the API. Asked once; `reload` asks again. */
    const loadTenants = (reload = false): Promise<void> => {
        if (tenantsLoaded.value && !reload) return Promise.resolve()
        if (pendingList) return pendingList

        pendingList = (async () => {
            const { statusCode, data } = await fetchStore.fetch('/tenants').get().json<ITenant[]>()

            if (statusCode.value === 200) {
                tenants.value       = data.value ?? []
                tenantsLoaded.value = true
            }

            pendingList = null
        })()

        return pendingList
    }

    let pendingId = ''

    /** Makes `id` the current tenant: GET /tenants/{id}, remembered for the next visit. */
    const select = async (id: string): Promise<void> => {
        if (!id || tenant.value?.id === id || pendingId === id) return

        pendingId        = id
        loadFailed.value = false

        const { statusCode, data } = await fetchStore.fetch(`/tenants/${id}`).get().json<ITenant>()

        if (pendingId !== id) return
        pendingId = ''

        if (statusCode.value === 200 && data.value) {
            tenant.value = data.value
            if (appStore.canOpenTenant(id)) remember(id)
        } else {
            loadFailed.value = true
        }
    }

    /** A saved tenant (brand, location, the provider's edit) replaces its copies. */
    const setTenant = (value: ITenant) => {
        if (tenant.value?.id === value.id) tenant.value = value

        tenants.value = tenants.value.map((item) => item.id === value.id ? value : item)
    }

    /** A deleted tenant leaves the list, and the remembered choice if it was that one. */
    const forget = (id: string) => {
        tenants.value = tenants.value.filter((item) => item.id !== id)

        if (tenant.value?.id === id) tenant.value = null
        if (storedTid.value === id) remember('')
    }

    const clear = () => {
        tenant.value        = null
        tenants.value       = []
        tenantsLoaded.value = false
        pendingId           = ''
    }

    return {
        tenant, tenants, tenantsLoaded, loadFailed, storedTid, rememberedTid, tid, contextTid,
        loadTenants, select, setTenant, forget, clear, remember,
    }
})
